/**
 * Connection health predicates shared by the Connections panel and the
 * once-per-break toast. Matches oauth.is_token_expired's 60s leeway so the
 * panel and the toast cannot disagree about "is this grant dead".
 */

import type { ConnectionRecord } from '../services/ddr.service';
import type { MCPGrant, MCPServer } from '../services/mcp.service';

export const CONNECTION_TOKEN_LEEWAY_MS = 60_000;

export type BrokenConnectionKind = 'native' | 'mcp';

export type BrokenConnection = {
    key: string;
    label: string;
    kind: BrokenConnectionKind;
};

const NATIVE_LABELS: Record<string, string> = {
    m365: 'Microsoft 365',
    github: 'GitHub',
    atlassian: 'Atlassian',
    salesforce: 'Salesforce',
};

const MCP_LABELS: Record<string, string> = {
    'bep-back-office': 'Back Office',
    insidetrack: 'InsideTrack',
    arrowstream: 'ArrowStream',
    'account-360': 'Account 360',
    cortex: 'Velma',
    'composio-gmail': 'Gmail',
    'composio-google-calendar': 'Google Calendar',
    'composio-google-drive': 'Google Drive',
};

export function nativeConnectionKey(service: string): string {
    return `native:${service}`;
}

export function mcpConnectionKey(serverId: string): string {
    return `mcp:${serverId}`;
}

export function nativeConnectionLabel(service: string): string {
    return NATIVE_LABELS[service] ?? service;
}

export function mcpConnectionLabel(serverName: string | null | undefined): string {
    const name = (serverName ?? '').trim();
    return MCP_LABELS[name] || name || 'a connector';
}

/** True when a stored native connection cannot be used until the user reconnects. */
export function nativeConnectionIsBroken(conn: ConnectionRecord): boolean {
    if (conn.revoked_at) {
        return false;
    }
    return conn.status === 'expired' || conn.status === 'error';
}

/**
 * True when an MCP grant cannot be refreshed and the user has to reconnect.
 * A still-valid access token, or an expired one that still has a refresh
 * token, is healthy: the next tool call refreshes it.
 */
export function mcpGrantIsBroken(grant: MCPGrant): boolean {
    if (grant.revoked_at) {
        return false;
    }
    const cfg = grant.user_auth_config;
    if (!cfg) {
        return true;
    }
    if (cfg.needs_reconnect || cfg.auth_state === 'needs_reconnect') {
        return true;
    }
    if (!cfg.has_access_token) {
        return true;
    }
    if (!cfg.expires_at) {
        return false;
    }
    const expiresAt = Date.parse(cfg.expires_at);
    if (Number.isNaN(expiresAt)) {
        return false;
    }
    const expired = Date.now() >= expiresAt - CONNECTION_TOKEN_LEEWAY_MS;
    return expired && !cfg.has_refresh_token;
}

export function collectBrokenConnections(args: {
    natives: ConnectionRecord[];
    grants: MCPGrant[];
    servers: MCPServer[];
    serversUnavailable?: boolean;
}): {
    broken: BrokenConnection[];
    healthyKeys: string[];
} {
    const broken: BrokenConnection[] = [];
    const healthyKeys: string[] = [];
    const seen = new Set<string>();

    for (const conn of args.natives) {
        const key = nativeConnectionKey(conn.service);
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        if (nativeConnectionIsBroken(conn)) {
            broken.push({ key, label: nativeConnectionLabel(conn.service), kind: 'native' });
        } else if (!conn.revoked_at) {
            healthyKeys.push(key);
        }
    }

    const serverById = new Map(args.servers.map(s => [s.id, s]));
    for (const grant of args.grants) {
        const serverId = grant.org_mcp_server_id;
        if (!serverId) {
            continue;
        }
        const key = mcpConnectionKey(serverId);
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        const server = serverById.get(serverId);
        if (!server) {
            if (args.serversUnavailable && grant.user_auth_config && mcpGrantIsBroken(grant)) {
                broken.push({ key, label: mcpConnectionLabel(null), kind: 'mcp' });
            }
            continue;
        }
        if (server.auth_type !== 'oauth2') {
            healthyKeys.push(key);
            continue;
        }
        if (mcpGrantIsBroken(grant)) {
            broken.push({ key, label: mcpConnectionLabel(server.name), kind: 'mcp' });
        } else {
            healthyKeys.push(key);
        }
    }

    return { broken, healthyKeys };
}

export function connectionBrokenToast(label: string): string {
    return `${label} is no longer connected. Open Settings → Connections and reconnect it so your DDRs can use it.`;
}
